import React, { useEffect, useState, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bell, CheckCheck, Trash2, Filter, AlertTriangle, CheckCircle2, Info,
  Inbox, Megaphone, Settings as SettingsIcon, Loader2, X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { getNotificationTargetUrl } from '../components/NotificationBell';

const API = process.env.REACT_APP_BACKEND_URL + '/api/notifications';

const CATEGORY_TINT = {
  approvals: 'bg-blue-100 text-blue-700 border-blue-200',
  payroll: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  statutory: 'bg-purple-100 text-purple-700 border-purple-200',
  lifecycle: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  attendance: 'bg-amber-100 text-amber-700 border-amber-200',
  security: 'bg-rose-100 text-rose-700 border-rose-200',
  system: 'bg-slate-100 text-slate-700 border-slate-200',
};
const SEVERITY_ICON = { info: Info, success: CheckCircle2, warning: AlertTriangle, critical: AlertTriangle };
const SEVERITY_COLOR = { info: 'text-blue-500', success: 'text-emerald-500', warning: 'text-amber-500', critical: 'text-rose-600' };

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleString();
};

const Notifications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [byCategory, setByCategory] = useState([]);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterRead, setFilterRead] = useState('all'); // all | unread | read
  const [loading, setLoading] = useState(true);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastForm, setBroadcastForm] = useState({
    target: 'tenant', role: 'employee', company_id: '', category: 'system',
    severity: 'info', title: '', message: '', link: '',
  });
  const [companies, setCompanies] = useState([]);
  const canBroadcast = ['tenant_admin', 'hr_admin', 'super_admin'].includes(user?.role);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCategory) params.append('category', filterCategory);
      if (filterRead === 'unread') params.append('is_read', 'false');
      else if (filterRead === 'read') params.append('is_read', 'true');
      params.append('limit', '200');
      const [list, count] = await Promise.all([
        axios.get(`${API}?${params}`, { withCredentials: true }),
        axios.get(`${API}/unread-count`, { withCredentials: true }),
      ]);
      setItems(list.data || []);
      setUnreadCount(count.data.total || 0);
      setByCategory(count.data.by_category || []);
    } catch (e) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterRead]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => {
    if (canBroadcast) {
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/companies`, { withCredentials: true })
        .then(({ data }) => setCompanies(data || []))
        .catch(() => setCompanies([]));
    }
  }, [canBroadcast]);

  const markRead = async (n) => {
    if (n.is_read) return;
    try {
      await axios.patch(`${API}/${n.id}/read`, null, { withCredentials: true });
      setItems((arr) => arr.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (_) { /* silent */ }
  };
  const markAll = async () => {
    try {
      await axios.post(`${API}/mark-all-read`, null, { withCredentials: true });
      toast.success('All marked as read');
      fetchAll();
    } catch (_) { toast.error('Failed'); }
  };
  const removeOne = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/${id}`, { withCredentials: true });
      setItems((arr) => arr.filter((x) => x.id !== id));
    } catch (_) { /* silent */ }
  };
  const clearAll = async () => {
    if (!window.confirm('Delete ALL notifications? This cannot be undone.')) return;
    try {
      await axios.post(`${API}/clear-all`, null, { withCredentials: true });
      toast.success('Cleared');
      fetchAll();
    } catch (_) { toast.error('Failed'); }
  };

  const handleBroadcast = async (e) => {
    e.preventDefault();
    if (!broadcastForm.title.trim() || !broadcastForm.message.trim()) {
      toast.error('Title and message are required');
      return;
    }
    try {
      const { data } = await axios.post(`${API}/broadcast`, broadcastForm, { withCredentials: true });
      toast.success(`Sent to ${data.sent} of ${data.recipient_count} user(s)`);
      setShowBroadcast(false);
      setBroadcastForm({ ...broadcastForm, title: '', message: '', link: '' });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Broadcast failed'); }
  };

  const onRowClick = async (n) => {
    const userRole = (user?.role || '').toLowerCase();
    const targetUrl = getNotificationTargetUrl(n, userRole);

    // Permission check for protected routes
    const isEmployee = userRole === 'employee';
    const isProtectedAdminRoute = ['/salary-increment-workbench', '/payroll-execution', '/hr-bench', '/kyc-corrections'].some(r => targetUrl.startsWith(r));
    
    if (isEmployee && isProtectedAdminRoute) {
      toast.error('Access Denied: You do not have permission to access this screen.');
      return;
    }

    try {
      navigate(targetUrl);
      if (!n.is_read) {
        await markRead(n);
      }
    } catch (err) {
      toast.error('Failed to navigate to target screen');
    }
  };

  return (
    <MainLayout>
      <div className="space-y-5" data-testid="notifications-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              <Bell className="inline-block w-7 h-7 mr-2 mb-1" /> Notifications
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              {unreadCount > 0 ? <><strong>{unreadCount}</strong> unread</> : 'All caught up'}
              {byCategory.length > 0 && (
                <span className="ml-3 text-xs text-slate-500">
                  {byCategory.map((c) => `${c.category}: ${c.count}`).join(' · ')}
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/notifications/preferences" className="inline-flex items-center h-10 px-3 rounded-md border border-slate-300 text-sm text-slate-700 hover:bg-slate-50" data-testid="prefs-link">
              <SettingsIcon className="w-4 h-4 mr-1.5" /> Preferences
            </Link>
            {canBroadcast && (
              <Button onClick={() => setShowBroadcast(true)} className="bg-[#0052FF] text-white hover:bg-[#0040CC]" data-testid="broadcast-btn">
                <Megaphone className="w-4 h-4 mr-1.5" /> Broadcast
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap bg-white border border-slate-200 rounded-xl p-3">
          <Filter className="w-4 h-4 text-slate-400" />
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            data-testid="filter-category"
            className="h-9 px-3 border border-slate-300 rounded-md text-sm bg-white"
          >
            <option value="">All categories</option>
            {Object.keys(CATEGORY_TINT).map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <div className="flex bg-slate-100 rounded-md p-0.5">
            {['all', 'unread', 'read'].map((k) => (
              <button
                key={k}
                onClick={() => setFilterRead(k)}
                data-testid={`filter-${k}`}
                className={`px-3 py-1 text-xs font-medium rounded ${filterRead === k ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600'}`}
              >
                {k}
              </button>
            ))}
          </div>
          <div className="ml-auto flex gap-2">
            {unreadCount > 0 && (
              <Button variant="outline" onClick={markAll} data-testid="mark-all-read-page">
                <CheckCheck className="w-4 h-4 mr-1" /> Mark all read
              </Button>
            )}
            {items.length > 0 && (
              <Button variant="outline" onClick={clearAll} className="text-red-600 hover:text-red-700" data-testid="clear-all-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Clear all
              </Button>
            )}
          </div>
        </div>

        {/* List */}
        <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden">
          {loading ? (
            <div className="text-center py-16"><Loader2 className="w-7 h-7 animate-spin text-slate-300 mx-auto" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <Inbox className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">No notifications match your filters</p>
            </div>
          ) : items.map((n) => {
            const Icon = SEVERITY_ICON[n.severity] || Info;
            return (
              <div
                key={n.id}
                onClick={() => onRowClick(n)}
                data-testid={`notif-row-${n.id}`}
                className={`group cursor-pointer flex items-start gap-3 p-4 hover:bg-slate-50 ${!n.is_read ? 'bg-blue-50/30' : ''}`}
              >
                <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${SEVERITY_COLOR[n.severity] || 'text-slate-400'}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-slate-900">{n.title}</p>
                    {!n.is_read && <span className="w-2 h-2 rounded-full bg-blue-500" />}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${CATEGORY_TINT[n.category] || CATEGORY_TINT.system}`}>
                      {n.category}
                    </span>
                    <span className="text-xs text-slate-400 ml-auto">{formatTime(n.created_at)}</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-1">{n.message}</p>
                  {n.link && (
                    <p className="text-xs text-[#0052FF] mt-1 font-mono">{n.link}</p>
                  )}
                </div>
                <button
                  onClick={(e) => removeOne(n.id, e)}
                  data-testid={`delete-${n.id}`}
                  className="p-1.5 text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>

        {/* Broadcast modal */}
        {showBroadcast && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowBroadcast(false)}>
            <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="broadcast-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Megaphone className="w-5 h-5 text-[#0052FF]" /> Broadcast Notification
                </h3>
                <button onClick={() => setShowBroadcast(false)}><X className="w-5 h-5 text-slate-400" /></button>
              </div>
              <form onSubmit={handleBroadcast} className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Send to</label>
                    <select
                      value={broadcastForm.target}
                      onChange={(e) => setBroadcastForm({ ...broadcastForm, target: e.target.value })}
                      data-testid="broadcast-target"
                      className="w-full border border-slate-300 rounded-md px-3 py-2"
                    >
                      <option value="tenant">All users in tenant</option>
                      <option value="company">A specific company</option>
                      <option value="role">A specific role</option>
                    </select>
                  </div>
                  {broadcastForm.target === 'role' && (
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">Role</label>
                      <select
                        value={broadcastForm.role}
                        onChange={(e) => setBroadcastForm({ ...broadcastForm, role: e.target.value })}
                        className="w-full border border-slate-300 rounded-md px-3 py-2"
                      >
                        {['tenant_admin', 'hr_admin', 'payroll_admin', 'attendance_admin', 'finance_reviewer', 'department_manager', 'employee'].map((r) => (
                          <option key={r} value={r}>{r.replace('_', ' ')}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {broadcastForm.target === 'company' && (
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">Company</label>
                      <select
                        value={broadcastForm.company_id}
                        onChange={(e) => setBroadcastForm({ ...broadcastForm, company_id: e.target.value })}
                        className="w-full border border-slate-300 rounded-md px-3 py-2"
                      >
                        <option value="">Pick a company</option>
                        {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Category</label>
                    <select
                      value={broadcastForm.category}
                      onChange={(e) => setBroadcastForm({ ...broadcastForm, category: e.target.value })}
                      className="w-full border border-slate-300 rounded-md px-3 py-2"
                    >
                      {Object.keys(CATEGORY_TINT).map((k) => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Severity</label>
                    <select
                      value={broadcastForm.severity}
                      onChange={(e) => setBroadcastForm({ ...broadcastForm, severity: e.target.value })}
                      className="w-full border border-slate-300 rounded-md px-3 py-2"
                    >
                      {['info', 'success', 'warning', 'critical'].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Title *</label>
                  <input
                    value={broadcastForm.title}
                    onChange={(e) => setBroadcastForm({ ...broadcastForm, title: e.target.value })}
                    data-testid="broadcast-title"
                    className="w-full border border-slate-300 rounded-md px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Message *</label>
                  <textarea
                    value={broadcastForm.message}
                    onChange={(e) => setBroadcastForm({ ...broadcastForm, message: e.target.value })}
                    rows={3}
                    data-testid="broadcast-message"
                    className="w-full border border-slate-300 rounded-md px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Link (optional)</label>
                  <input
                    value={broadcastForm.link}
                    onChange={(e) => setBroadcastForm({ ...broadcastForm, link: e.target.value })}
                    placeholder="/payroll/payslips"
                    className="w-full border border-slate-300 rounded-md px-3 py-2 font-mono"
                  />
                </div>
                <Button type="submit" data-testid="broadcast-send-btn" className="w-full bg-[#0052FF] text-white hover:bg-[#0040CC]">
                  Send Broadcast
                </Button>
              </form>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default Notifications;
